import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { DARK_MODE_ENABLED } from '../config/features';

const ThemeContext = createContext();

const THEME_KEY = 'awfm-theme';
const CONTRAST_KEY = 'awfm-contrast';
const MOTION_KEY = 'awfm-motion';

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    // While dark mode is disabled, always start light — ignore any saved 'dark'.
    if (!DARK_MODE_ENABLED) return 'light';
    return localStorage.getItem(THEME_KEY) || 'light';
  });

  const [contrast, setContrast] = useState(() => {
    return localStorage.getItem(CONTRAST_KEY) || 'normal';
  });

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // 'reduce' turns off animations app-wide (on top of the OS setting).
  const [motion, setMotion] = useState(() => {
    try { return localStorage.getItem(MOTION_KEY) || 'normal'; } catch { return 'normal'; }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-motion', motion);
    try { localStorage.setItem(MOTION_KEY, motion); } catch { /* storage blocked */ }
  }, [motion]);

  useEffect(() => {
    // Apply contrast to document
    document.documentElement.setAttribute('data-contrast', contrast);
    localStorage.setItem(CONTRAST_KEY, contrast);
  }, [contrast]);

  useEffect(() => {
    if (!DARK_MODE_ENABLED) return undefined;
    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-switch if user hasn't set a preference
      const saved = localStorage.getItem(THEME_KEY);
      if (!saved) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    if (!DARK_MODE_ENABLED) return;
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  const toggleContrast = useCallback(() => {
    setContrast(prev => prev === 'normal' ? 'high' : 'normal');
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      isDark: theme === 'dark',
      contrast,
      toggleContrast,
      isHighContrast: contrast === 'high',
      setContrast,
      reduceMotion: motion === 'reduce',
      setReduceMotion: (on) => setMotion(on ? 'reduce' : 'normal'),
    }),
    [theme, toggleTheme, contrast, toggleContrast, motion],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
